/**
 * DirectoryClaimPublicService — zero-auth, public directory claim flow service.
 *
 * Extends PublicApiSingleton (RequestType.PUBLIC, no credentials).
 * Caching is disabled on every method (claim-token state).
 *
 * Wraps:
 *   - GET  /api/public/directory/claim/:token        (public summary)
 *   - POST /api/public/directory/claim/:token/accept (bind owner, requires auth)
 */
import { PublicApiSingleton } from '../providers/base/PublicApiSingleton';
import type { UpgradeOptions, UpgradeTierOption } from './DirectoryPresenceUpgradeService';

export interface DirectoryClaimSummary {
  seedId: string;
  slug: string;
  businessName: string;
  category: string;
  city: string;
  state: string;
  address: string;
  zipCode: string | null;
  phone: string | null;
  website: string | null;
  email: string | null;
  /** Listing logo — the tenant profile's logo_url; owner can replace it during claim. */
  logoUrl?: string | null;
  notes: string | null;
  latitude: number | null;
  longitude: number | null;
  primaryCategory: string | null;
  secondaryCategories: string[];
  businessHours: any;
  socialLinks?: { platform: string; url: string }[];
  snapEbtReported: boolean;
  /** Sourced attribute chips (migration 267) — the verify step lets the
   *  owner accept or reject each one. */
  attributes?: ClaimListingAttribute[];
  /** Stamp of the owner's claim-time consent (migration 274). */
  ownerVerifiedAt?: string | null;
  /** Owner-typed labels awaiting operator acceptance (pending only). */
  ownerProposedCategories?: { label: string; role: 'primary' | 'secondary'; status: string }[];
  isExpired: boolean;
  isConsumed: boolean;
  expiresAt: string;
  consumedAt: string | null;
  /** Latest operator-review state for this token — 'rejected' renders an
   *  honest "not approved" state rather than the misleading Already Claimed
   *  screen; 'pending' restores the review screen on revisit. */
  claimRequestStatus?: 'pending' | 'approved' | 'rejected' | null;
  /** A live claimant email verification exists for this token (migration
   *  358) — restore the "check your email" screen on revisit. */
  claimEmailVerificationPending?: boolean;
  /** Masked email the live verification was sent to. */
  claimVerificationSentTo?: string | null;
  /** Wedge-fidelity verdict (project-phase spec §4/§6). 'misaligned' routes
   *  the landing page to the inquiry path instead of the claim form. */
  seedFidelity?: 'aligned' | 'thin' | 'misaligned' | 'unknown';
}

export interface ClaimListingAttribute {
  key: string;
  label: string;
  sourcePlatform?: string;
  sourceUrl?: string;
  asOf?: string;
  ownerConfirmed?: boolean;
}

/**
 * Claim-time verification payload (migration 274) — the contract of consent.
 * `confirmed` must be true; the claim cannot initiate without it.
 */
export interface OwnerClaimVerification {
  primaryCategory: string;
  secondaryCategories: string[];
  /** Owner-typed labels not in the vocab — diverted to operator review. */
  proposedCategories?: string[];
  /** Final attribute set the owner accepts (omitted sourced chips = rejected). */
  attributes: { key?: string; label: string }[];
  confirmed: true;
}

export interface DirectoryClaimAcceptResult {
  success: boolean;
  tenantId?: string;
  seedId?: string;
  message?: string;
  error?: string;
  /** Platform user tokens — set when a customer is promoted to platform user */
  userTokens?: { accessToken: string; refreshToken: string };
  /** True when the promoted user has no password (OAuth-only customer) */
  requiresPasswordSetup?: boolean;
  /** The platform user ID that was created or linked */
  platformUserId?: string;
  /** Gateway upgrade preview (claim handoff spec) — embedded by the accept response so the success screen can render Entry Presence mode cards without a platform (Auth0) session */
  currentTier?: UpgradeOptions['currentTier'];
  isGatewayUpgrade?: boolean;
  upgradeOptions?: UpgradeTierOption[];
  solutions?: UpgradeOptions['solutions'];
}

export interface DirectoryClaimInitiateResult {
  success: boolean;
  verificationRequired?: boolean;
  sentTo?: string;
  operatorApprovalRequired?: boolean;
  /** Claimant email confirmation needed before the request is filed —
   *  a verification link was sent to `sentTo` (migration 358). */
  claimantVerificationRequired?: boolean;
  /** True when the claim cannot initiate without the owner verification
   *  (categories + attributes consent gate, migration 274). */
  ownerVerificationRequired?: boolean;
  /** Owner-typed labels diverted to operator review on this submit. */
  proposedCategories?: string[];
  error?: string;
}

export interface DirectoryClaimInitiateInput {
  claimantFirstName?: string;
  claimantMiddleName?: string;
  claimantLastName?: string;
  claimantPhone?: string;
  claimantBusinessAddress?: string;
  /** Customer email (frontend fallback for identity matching) */
  customerEmail?: string;
  /** Customer ID (frontend fallback for identity matching) */
  customerId?: string;
  /** Claim-time verification (migration 274) — required on first submit. */
  verification?: OwnerClaimVerification;
}

export class DirectoryClaimPublicService extends PublicApiSingleton {
  private static instance: DirectoryClaimPublicService;

  private constructor() {
    super('directory-claim-public', { ttl: 0 });
  }

  public static getInstance(): DirectoryClaimPublicService {
    if (!DirectoryClaimPublicService.instance) {
      DirectoryClaimPublicService.instance = new DirectoryClaimPublicService();
    }
    return DirectoryClaimPublicService.instance;
  }

  /** GET /api/public/directory/claim/:token — public token summary */
  async getClaimSummary(token: string): Promise<DirectoryClaimSummary | null> {
    try {
      const result = await this.makeDefaultRequest<any>(
        `/api/public/directory/claim/${encodeURIComponent(token)}`,
        { method: 'GET' },
        undefined,
        0,
      );
      if (!result.success) return null;
      const data = result.data?.data ?? result.data;
      if (!data || !(data as any).summary) return null;
      return (data as any).summary as DirectoryClaimSummary;
    } catch {
      return null;
    }
  }

  /** POST /api/public/directory/claim/:token/initiate — initiate claim (sends OTP if required) */
  async initiateClaim(token: string, claimant?: DirectoryClaimInitiateInput): Promise<DirectoryClaimInitiateResult> {
    try {
      const result = await this.makeDefaultRequest<any>(
        `/api/public/directory/claim/${encodeURIComponent(token)}/initiate`,
        {
          method: 'POST',
          body: JSON.stringify(claimant || {}),
          headers: this.getCustomerAuthHeaders(),
        },
        undefined,
        0,
      );
      if (!result.success) {
        const error = typeof result.error === 'string' ? result.error : 'unknown';
        return { success: false, error };
      }
      const data = result.data?.data ?? result.data;
      return (data as any) ?? { success: false, error: 'unknown' };
    } catch (err: any) {
      return { success: false, error: err?.message || 'unknown' };
    }
  }

  /** PUT /api/public/directory/claim/:token/listing — owner pre-approval edits */
  async updateListing(token: string, fields: any): Promise<{ success: boolean; error?: string }> {
    try {
      const result = await this.makeDefaultRequest<any>(
        `/api/public/directory/claim/${encodeURIComponent(token)}/listing`,
        {
          method: 'PUT',
          body: JSON.stringify(fields),
          headers: this.getCustomerAuthHeaders(),
        },
        undefined,
        0,
      );
      if (!result.success) {
        const error = typeof result.error === 'string' ? result.error : 'unknown';
        return { success: false, error };
      }
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err?.message || 'unknown' };
    }
  }

  /** POST /api/public/directory/claim/:token/logo — owner logo upload.
   *  Writes tenant_business_profiles_list.logo_url (the tenant-logo
   *  destination) + the listing's logo_url via the same service path the
   *  operator seed-logo upload uses. */
  async uploadClaimLogo(
    token: string,
    dataUrl: string,
    contentType: string,
  ): Promise<{ success: boolean; url?: string; error?: string }> {
    try {
      const result = await this.makeDefaultRequest<any>(
        `/api/public/directory/claim/${encodeURIComponent(token)}/logo`,
        {
          method: 'POST',
          body: JSON.stringify({ dataUrl, contentType }),
          headers: this.getCustomerAuthHeaders(),
        },
        undefined,
        0,
      );
      if (!result.success) {
        const error = typeof result.error === 'string' ? result.error : 'unknown';
        return { success: false, error };
      }
      return { success: true, url: (result.data?.url ?? undefined) as string | undefined };
    } catch (err: any) {
      return { success: false, error: err?.message || 'unknown' };
    }
  }

  /** GET /api/public/directory/attribute-definitions?category= — predefined
   *  attribute chips offered to the owner during claim-time verification. */
  async getAttributeDefinitions(category?: string): Promise<{ attributeKey: string; label: string; groupKey: string }[]> {
    try {
      const qs = category ? `?category=${encodeURIComponent(category)}` : '';
      const result = await this.makeDefaultRequest<any>(
        `/api/public/directory/attribute-definitions${qs}`,
        { method: 'GET' },
        undefined,
        0,
      );
      if (!result.success) return [];
      const data = result.data?.data ?? result.data;
      return data?.definitions ?? [];
    } catch {
      return [];
    }
  }

  /** GET /api/public/directory/claim/:token/slug-check — token-gated free-text
   *  availability for the picker's "type your own" entry. */
  async checkSlugAvailability(
    token: string,
    slug: string
  ): Promise<{ available: boolean; own: boolean; code?: string } | null> {
    try {
      const result = await this.makeDefaultRequest<any>(
        `/api/public/directory/claim/${encodeURIComponent(token)}/slug-check?slug=${encodeURIComponent(slug)}`,
        { method: 'GET' },
        undefined,
        0,
      );
      if (!result.success) return null;
      const data = result.data?.data ?? result.data;
      if (!data) return null;
      return { available: !!data.isAvailable, own: !!data.isOwnSlug, code: data.code };
    } catch {
      return null;
    }
  }

  /** GET /api/public/directory/claim/:token/slug-patterns — available slugs */
  async getSlugPatterns(token: string): Promise<{ pattern: string; slug: string; isAvailable: boolean; isOwnSlug: boolean; description: string }[]> {
    try {
      const result = await this.makeDefaultRequest<any>(
        `/api/public/directory/claim/${encodeURIComponent(token)}/slug-patterns`,
        { method: 'GET' },
        undefined,
        0,
      );
      if (!result.success) return [];
      const data = result.data?.data ?? result.data;
      return data?.patterns || [];
    } catch {
      return [];
    }
  }

  /**
   * POST /api/public/directory/claim-requests/verify — confirm the
   * claimant's email link (migration 358) and file the pending request.
   * `claimToken` comes back so the page can link the claimant to their
   * claim screen (proof upload, pending status).
   */
  async verifyClaimRequestEmail(
    token: string,
  ): Promise<{
    success: boolean;
    error?: string;
    requestId?: string;
    claimToken?: string;
    businessName?: string;
  }> {
    try {
      const result = await this.makeDefaultRequest<any>(
        '/api/public/directory/claim-requests/verify',
        {
          method: 'POST',
          body: JSON.stringify({ token }),
        },
        undefined,
        0,
      );

      if (!result.success) {
        const error = typeof result.error === 'string' ? result.error : 'unknown';
        const data = result.data?.data ?? result.data;
        return { success: false, error, claimToken: (data as any)?.claimToken };
      }

      const data = result.data?.data ?? result.data;
      return (data as any) ?? { success: false, error: 'unknown' };
    } catch (err: any) {
      const body = err?.data || err?.body || {};
      return { success: false, error: body?.error || err?.message || 'unknown', claimToken: body?.claimToken };
    }
  }

  /** POST /api/public/directory/claim/:token/accept — bind owner (requires auth) */
  async acceptClaim(token: string, otpCode?: string): Promise<DirectoryClaimAcceptResult> {
    try {
      const result = await this.makeDefaultRequest<any>(
        `/api/public/directory/claim/${encodeURIComponent(token)}/accept`,
        { method: 'POST', body: JSON.stringify({ otpCode }), headers: this.getCustomerAuthHeaders() },
        undefined,
        0,
      );
      if (!result.success) {
        const error = typeof result.error === 'string' ? result.error : 'unknown';
        return { success: false, error };
      }
      const data = result.data?.data ?? result.data;
      return (data as any) ?? { success: false, error: 'unknown' };
    } catch (err: any) {
      return { success: false, error: err?.message || 'unknown' };
    }
  }

  /**
   * Build auth headers from the customer JWT in localStorage.
   * The claim initiate/accept endpoints use optionalCustomerAuth middleware
   * on the backend, so sending the customer token allows the server to
   * capture customer_id + email for operator-approval claim requests.
   */
  private getCustomerAuthHeaders(): Record<string, string> {
    if (typeof window === 'undefined') return {};
    const headers: Record<string, string> = {};
    const customerToken = localStorage.getItem('customer_auth_token');
    if (customerToken) {
      headers['Authorization'] = `Bearer ${customerToken}`;
    }
    // Also send customer identity from the cache (more reliable than JWT
    // parsing for public routes where the singleton may strip headers)
    try {
      const identity = localStorage.getItem('customer_identity_cache');
      if (identity) {
        const parsed = JSON.parse(identity);
        if (parsed?.id) headers['x-customer-id'] = parsed.id;
        if (parsed?.email) headers['x-customer-email'] = parsed.email;
      }
    } catch {
      // ignore parse errors
    }
    return headers;
  }

  /** POST /api/public/directory/claim/:token/proof — upload proof of ownership (multipart) */
  async uploadProofAttachment(
    token: string,
    file: File,
  ): Promise<{ attachmentId: string; fileName: string; fileType: string; fileSize: number }> {
    const formData = new FormData();
    formData.append('file', file);

    const result = await this.makeDefaultRequest<any>(
      `/api/public/directory/claim/${encodeURIComponent(token)}/proof`,
      {
        method: 'POST',
        body: formData,
        headers: this.getCustomerAuthHeaders(),
      },
      undefined,
      0,
    );
    if (!result.success) {
      throw new Error(typeof result.error === 'string' ? result.error : 'Failed to upload proof');
    }
    const data = result.data?.data ?? result.data;
    return {
      attachmentId: data.attachmentId,
      fileName: data.fileName,
      fileType: data.fileType,
      fileSize: data.fileSize,
    };
  }
}

const directoryClaimPublicService = DirectoryClaimPublicService.getInstance();
export default directoryClaimPublicService;
